import { inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { CompanyService } from 'src/app/company/company.service';
import { IOperatorClass } from './OperatorClass';
import { OperatorInput, OperatorOutput } from './models/models';

interface SheetCompanyRow {
    pdbNo: string;
    naic: string;
    ambest: string;
}

export class GroupReportingOperator implements IOperatorClass {
    private readonly companyService = inject(CompanyService);

    private getCellValue(worksheet: any, row: number, col: number): string {
        const value = worksheet.rows(row).cells(col).value;
        return value != null ? String(value).trim() : '';
    }

    // The downloaded template stores its type in the Meta sheet (index 9) at cell A90.
    // 'Plane' keeps the original Group Reporting layout; otherwise the sheet has an
    // extra "Company Name" column which shifts NAIC and AM Best one column to the right.
    private getTemplateType(workbook: any): string {
        const metaSheet = workbook.worksheets(9);
        if (!metaSheet) {
            return 'Plane';
        }
        const value = this.getCellValue(metaSheet, 89, 0);
        return value || 'Plane';
    }

    async runOperator(operatorInput: OperatorInput): Promise<OperatorOutput> {

        const workbook = operatorInput.workbook;
        if (!workbook) {
            return {
                status: 'error',
                message: ['Workbook not found.'],
                operatorInput,
            };
        }

        const load$ = this.companyService.loadGroupReport(
            operatorInput.prfyear,
            operatorInput.prfnumber ?? '',
            operatorInput.prfGroup,
        );
        if (!load$) {
            return {
                status: 'error',
                message: ['Failed to load group company details.'],
                operatorInput,
            };
        }

        let groupResp: any;
        try {
            groupResp = await firstValueFrom(load$);
        } catch {
            return {
                status: 'error',
                message: ['Failed to load group company details.'],
                operatorInput,
            };
        }

        if (groupResp?.message !== 'Success') {
            return {
                status: 'error',
                message: ['Failed to load group company details.'],
                operatorInput,
            };
        }

        const groupCompanyDetails: any[] = groupResp.groupcompanydetails ?? [];

        const worksheet = workbook.worksheets(1);
        if (!worksheet) {
            return {
                status: 'error',
                message: ['Group Reporting sheet not found.'],
                operatorInput,
            };
        }

        const companyReportRows: SheetCompanyRow[] = [];
        const isGroupSpecific =
            this.getTemplateType(workbook).toLowerCase() === 'groupspecific';
        // Plain:         PDB(0), NAIC(1), AM Best(2)
        // GroupSpecific: PDB(1), Company Name(2), Include(3), NAIC(4), AM Best(5)
        const pdbCol = isGroupSpecific ? 1 : 0;
        const naicCol = isGroupSpecific ? 4 : 1;
        const ambestCol = isGroupSpecific ? 5 : 2;
        const includeCol = isGroupSpecific ? 3 : -1;

        let rowIndex = 1;
        while (true) {
            const pdbNo = this.getCellValue(worksheet, rowIndex, pdbCol);
            if (!pdbNo) break;

            const naic = this.getCellValue(worksheet, rowIndex, naicCol);
            const ambest = this.getCellValue(worksheet, rowIndex, ambestCol);

            // For group specific templates, only save companies ticked "Yes" in the
            // Include column AND with NAIC or AM Best entered; skip the rest entirely
            // (leave their state unchanged).
            if (includeCol >= 0) {
                const include = this.getCellValue(worksheet, rowIndex, includeCol).toLowerCase();
                if (include !== 'yes' || (!naic && !ambest)) {
                    rowIndex++;
                    continue;
                }
            } else {
                // For plain templates, only save companies with NAIC or AM Best entered;
                // skip the rest entirely (leave their state unchanged).
                if (!naic && !ambest) {
                    rowIndex++;
                    continue;
                }
            }

            companyReportRows.push({ pdbNo, naic, ambest });
            rowIndex++;
        }


        if (companyReportRows.length === 0) {
            return {
                status: 'error',
                message: ['Please select at least one company for group reporting to continue.'],
                operatorInput,
            };
        }

        const errors: string[] = [];
        for (const row of companyReportRows) {
            const match = groupCompanyDetails.find(
                (c: any) => c.No?.trim() === row.pdbNo,
            );

            if (!match) {
                errors.push(`Company ${row.pdbNo} was not found in the group.`);
                continue;
            }

            const takenBy = match.TakenBy?.trim() ?? '';
            if (takenBy && takenBy !== operatorInput.prfnumber?.trim()) {
                errors.push(
                    `Company ${row.pdbNo} is already group reported under ${takenBy}.`,
                );
                continue;
            }

            if (!row.naic && !row.ambest) {
                errors.push(`NAIC and AM Best are missing for company ${row.pdbNo}.`);
            }
        }

        if (errors.length > 0) {
            return { status: 'error', message: errors, operatorInput };
        }

        const updatedList = groupCompanyDetails.map((company: any) => {
            const sheetEntry = companyReportRows.find((r) => r.pdbNo === company.No?.trim());
            if (sheetEntry) {
                return {
                    ...company,
                    chkvalue: true,
                    TakenBy: operatorInput.prfnumber ?? '',
                    NAIC: sheetEntry.naic || company.NAIC,
                    AMBest: sheetEntry.ambest || company.AMBest,
                };
            }
            return {
                ...company,
                chkvalue: company.chkvalue === 'true' || company.chkvalue === true,
            };
        });

        const save$ = this.companyService.SaveGroupReport(
            operatorInput.prfyear,
            operatorInput.prfnumber ?? '',
            updatedList,
            operatorInput.prfGroup,
            '',
        );
        if (!save$) {
            return {
                status: 'error',
                message: ['Failed to save group reporting details.'],
                operatorInput,
            };
        }

        let saveResp: any;
        try {
            saveResp = await firstValueFrom(save$);
        } catch {
            return {
                status: 'error',
                message: ['Failed to save group reporting details.'],
                operatorInput,
            };
        }

        if (saveResp?.message !== 'Success') {
            return {
                status: 'error',
                message: [
                    saveResp?.status_msg ?? 'Failed to save group reporting details.',
                ],
                operatorInput,
            };
        }

        return { status: 'success', operatorInput };
    }
}
